import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, assetsAPI, transcribeAPI, researchAPI } from '../lib/api';
import { AppHeader } from '../components/layout/AppHeader';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Lightbulb, FileText, Clock, User, Mic, Sparkles, ChevronRight,
  ChevronLeft, Check, Play, Pause, Loader2, ArrowRight, Wand2,
  Volume2, Timer, Edit3, Upload, Camera, Square, MicOff, Video as VideoIcon,
  Globe, RefreshCw, TrendingUp, Newspaper, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const ALL_STEPS = [
  { key: 'basics', label: 'Project basics', icon: FileText },
  { key: 'content', label: 'Content input', icon: Edit3 },
  { key: 'duration', label: 'Duration', icon: Timer },
  { key: 'script_review', label: 'Review script', icon: Wand2 },
  { key: 'avatar', label: 'Avatar', icon: User },
  { key: 'voice', label: 'Voice', icon: Mic },
  { key: 'generate', label: 'Generate', icon: Sparkles },
];

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'sales', label: 'Sales' },
  { value: 'explainer', label: 'Explainer' },
  { value: 'social', label: 'Social' },
];

export default function NewProjectPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    input_mode: 'idea',
    input_text: '',
    duration_mode: '60',
    tone: 'professional',
    cta: '',
    avatar_id: null,
    voice_id: null,
    background_type: 'normal',
  });

  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [avatarSearch, setAvatarSearch] = useState('');
  const [voiceSearch, setVoiceSearch] = useState('');
  const [playingVoice, setPlayingVoice] = useState(null);
  const [audioRef] = useState(new Audio());

  // Project creation state
  const [projectId, setProjectId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedScript, setGeneratedScript] = useState('');
  const [scriptEditable, setScriptEditable] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [renderingVideo, setRenderingVideo] = useState(false);

  // Script review state
  const [scriptGenerating, setScriptGenerating] = useState(false);
  const [scriptReviewed, setScriptReviewed] = useState(false);
  const [reviewedScript, setReviewedScript] = useState('');

  // Dynamic steps: skip script_review for "script" mode
  const needsScriptReview = formData.input_mode === 'idea' || formData.input_mode === 'research';
  const STEPS = needsScriptReview
    ? ALL_STEPS
    : ALL_STEPS.filter((s) => s.key !== 'script_review');

  // Get the current step key
  const currentStepKey = STEPS[step]?.key;

  // Recording state
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  // Smart Research state
  const [researchData, setResearchData] = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Load avatars when reaching avatar step
  useEffect(() => {
    if (currentStepKey === 'avatar' && avatars.length === 0) {
      loadAvatars();
    }
  }, [currentStepKey]);

  // Load voices when reaching voice step
  useEffect(() => {
    if (currentStepKey === 'voice' && voices.length === 0) {
      loadVoices();
    }
  }, [currentStepKey]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      audioRef.pause();
      audioRef.src = '';
      stopRecording();
    };
  }, []);

  // Stop any active recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    setIsRecordingAudio(false);
    setIsRecordingVideo(false);
  }, []);

  // Transcribe a file (audio or video)
  const handleTranscribeFile = async (file) => {
    if (!file) return;
    setTranscribing(true);
    try {
      const res = await transcribeAPI.transcribe(file);
      const text = res.data.text;
      if (text) {
        update('input_text', (formData.input_text ? formData.input_text + '\n\n' : '') + text);
        toast.success('Transcription complete!');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Transcription failed. Please type manually.';
      toast.error(msg);
    } finally {
      setTranscribing(false);
    }
  };

  // Handle file upload (audio/video)
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) handleTranscribeFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Start audio recording
  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], 'recording.webm', { type: 'audio/webm' });
        handleTranscribeFile(file);
        stream.getTracks().forEach(t => t.stop());
      };

      recorder.start();
      setIsRecordingAudio(true);
      toast.info('Recording... Click stop when done.');
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone access.');
    }
  };

  // Stop audio recording
  const stopAudioRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecordingAudio(false);
  };

  // Start video recording (webcam)
  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
      mediaStreamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const file = new File([blob], 'recording.webm', { type: 'video/webm' });
        handleTranscribeFile(file);
        stream.getTracks().forEach(t => t.stop());
        if (videoPreviewRef.current) videoPreviewRef.current.srcObject = null;
      };

      recorder.start();
      // Set state FIRST so the video element renders, then connect stream in useEffect
      setIsRecordingVideo(true);
      toast.info('Recording video... Click stop when done.');
    } catch (err) {
      toast.error('Camera access denied. Please allow camera access.');
    }
  };

  // Connect video stream to preview element when recording starts
  useEffect(() => {
    if (isRecordingVideo && videoPreviewRef.current && mediaStreamRef.current) {
      videoPreviewRef.current.srcObject = mediaStreamRef.current;
      videoPreviewRef.current.play().catch(() => {});
    }
  }, [isRecordingVideo]);

  // Stop video recording
  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecordingVideo(false);
  };

  // Smart Research functions
  const runSmartResearch = async () => {
    setResearchLoading(true);
    setResearchData(null);
    setSelectedIdea(null);
    try {
      // Start research (returns immediately)
      const startRes = await researchAPI.start();
      const taskId = startRes.data.task_id;
      
      // Poll until complete
      let attempts = 0;
      const maxAttempts = 60; // ~2 min max
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 2000));
        const pollRes = await researchAPI.poll(taskId);
        if (pollRes.data.status === 'completed') {
          setResearchData(pollRes.data.result);
          setExpandedCategory(pollRes.data.result?.groups?.[0]?.category || null);
          toast.success('Research complete! Pick an idea below.');
          return;
        } else if (pollRes.data.status === 'failed') {
          throw new Error(pollRes.data.detail || 'Research failed');
        }
        attempts++;
      }
      throw new Error('Research timed out. Please try again.');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Smart research failed. Please try again.';
      toast.error(detail);
    } finally {
      setResearchLoading(false);
    }
  };

  const selectResearchIdea = (idea) => {
    setSelectedIdea(idea);
    // Populate the input text with the idea title + hook + key points
    const ideaText = `${idea.title}\n\n${idea.hook}\n\nKey points:\n${idea.key_points?.map(p => `- ${p}`).join('\n') || ''}\n\nCall to action: ${idea.suggested_cta || ''}`;
    update('input_text', ideaText);
    // Keep input_mode as 'research' - it flows through script generation like 'idea'
    toast.success('Idea selected! You can edit it below, then proceed.');
  };

  // Auto-trigger research when entering content step with research mode
  useEffect(() => {
    if (currentStepKey === 'content' && formData.input_mode === 'research' && !researchData && !researchLoading) {
      runSmartResearch();
    }
  }, [currentStepKey, formData.input_mode]);

  // Trigger script generation when entering the script_review step
  const generateScriptForReview = async () => {
    if (scriptGenerating || scriptReviewed) return;
    setScriptGenerating(true);
    setReviewedScript('');
    try {
      // Create project first if not already created
      let pid = projectId;
      if (!pid) {
        const res = await projectsAPI.create(formData);
        pid = res.data.project._id;
        setProjectId(pid);
      }
      
      // Trigger script generation
      await projectsAPI.generateScript(pid);
      toast.info('Generating script from your idea...');
      
      // Poll for script completion
      const project = await pollProjectStatus(pid, 'script_ready');
      if (project?.script_text) {
        setReviewedScript(project.script_text);
        setScriptReviewed(true);
        toast.success('Script generated! Review and edit below.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Script generation failed';
      toast.error(msg);
    } finally {
      setScriptGenerating(false);
    }
  };

  useEffect(() => {
    if (currentStepKey === 'script_review' && needsScriptReview && !scriptReviewed && !scriptGenerating) {
      generateScriptForReview();
    }
  }, [currentStepKey]);



  const loadAvatars = async () => {
    setLoadingAvatars(true);
    try {
      const res = await assetsAPI.avatars();
      setAvatars(res.data.avatars || []);
    } catch (err) {
      toast.error('Failed to load avatars');
    } finally {
      setLoadingAvatars(false);
    }
  };

  const loadVoices = async () => {
    setLoadingVoices(true);
    try {
      const res = await assetsAPI.voices();
      setVoices(res.data.voices || []);
    } catch (err) {
      toast.error('Failed to load voices');
    } finally {
      setLoadingVoices(false);
    }
  };

  const update = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    const key = currentStepKey;
    switch (key) {
      case 'basics': return formData.name.trim().length > 0;
      case 'content': return formData.input_text.trim().length > 0;
      case 'duration': return true;
      case 'script_review': return scriptReviewed && reviewedScript.trim().length > 0;
      case 'avatar': return !!formData.avatar_id;
      case 'voice': return !!formData.voice_id;
      case 'generate': return true;
      default: return true;
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleVoicePreview = (voiceId, previewUrl) => {
    if (playingVoice === voiceId) {
      audioRef.pause();
      setPlayingVoice(null);
    } else {
      audioRef.src = previewUrl;
      audioRef.play().catch(() => {});
      setPlayingVoice(voiceId);
      audioRef.onended = () => setPlayingVoice(null);
    }
  };

  // Create project and start generation
  const handleCreateAndGenerate = async () => {
    try {
      let pid = projectId;
      
      // Create project if not already created (happens for 'script' mode)
      if (!pid) {
        const res = await projectsAPI.create(formData);
        pid = res.data.project._id;
        setProjectId(pid);
      } else {
        // Project was created early (in script review step) — update with final avatar/voice/background
        await projectsAPI.update(pid, {
          avatar_id: formData.avatar_id,
          voice_id: formData.voice_id,
          background_type: formData.background_type,
          duration_mode: formData.duration_mode,
          tone: formData.tone,
          cta: formData.cta,
        });
      }

      if (formData.input_mode === 'idea' || formData.input_mode === 'research') {
        // Script was already generated & reviewed in the review step
        // Approve the (potentially edited) reviewed script
        if (reviewedScript) {
          await projectsAPI.approveScript(pid, { script_text: reviewedScript });
        }
      } else {
        // Script mode - approve the user-provided script directly
        await projectsAPI.approveScript(pid, { script_text: formData.input_text });
        setGeneratedScript(formData.input_text);
      }

      // Render video directly — HeyGen handles TTS internally using text input
      setRenderingVideo(true);
      await projectsAPI.renderVideo(pid);
      toast.success('Video rendering started! This may take 2-4 minutes.');
      
      // Navigate to project detail page for status tracking
      navigate(`/projects/${pid}`);
    } catch (err) {
      setGenerating(false);
      setGeneratingAudio(false);
      setRenderingVideo(false);
      const msg = err.response?.data?.detail || 'Something went wrong';
      toast.error(msg);
      if (projectId) navigate(`/projects/${projectId}`);
    }
  };

  const pollProjectStatus = async (pid, targetStatus, maxPolls = 60) => {
    for (let i = 0; i < maxPolls; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await projectsAPI.get(pid);
        const project = res.data.project;
        
        if (project.status === targetStatus) {
          if (project.script_text) setGeneratedScript(project.script_text);
          return project;
        }
        if (project.status === 'failed') {
          throw new Error(project.error || 'Generation failed');
        }
      } catch (err) {
        if (err.message?.includes('failed') || err.message?.includes('Generation')) throw err;
      }
    }
    throw new Error('Timed out waiting for generation');
  };

  // Approve edited script
  const handleApproveScript = async () => {
    if (!projectId) return;
    try {
      await projectsAPI.approveScript(projectId, { script_text: generatedScript });
      setScriptEditable(false);
      toast.success('Script approved');
    } catch (err) {
      toast.error('Failed to approve script');
    }
  };

  const filteredAvatars = avatars.filter((a) =>
    a.avatar_name?.toLowerCase().includes(avatarSearch.toLowerCase())
  );

  const filteredVoices = voices.filter((v) =>
    v.name?.toLowerCase().includes(voiceSearch.toLowerCase()) ||
    v.accent?.toLowerCase().includes(voiceSearch.toLowerCase()) ||
    v.gender?.toLowerCase().includes(voiceSearch.toLowerCase())
  );

  const wordCount = formData.input_text.trim().split(/\s+/).filter(Boolean).length;
  const charCount = formData.input_text.length;

  const isProcessing = generating || generatingAudio || renderingVideo;

  return (
    <div className="min-h-screen bg-background app-noise">
      <AppHeader showNewProject={false} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 relative z-10">
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold tracking-tight">New project</h1>
          <p className="text-sm text-muted-foreground mt-1">Create a new avatar video</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Left stepper */}
          <div className="hidden lg:block">
            <Card className="rounded-xl p-4 sticky top-20 shadow-[0_1px_0_rgba(15,23,42,0.04),0_14px_30px_rgba(15,23,42,0.06)]">
              <nav className="space-y-1" data-testid="new-project-stepper">
                {STEPS.map((s, idx) => {
                  const isActive = idx === step;
                  const isCompleted = idx < step;
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.key}
                      onClick={() => idx <= step && setStep(idx)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors
                        ${isActive ? 'bg-[hsl(38,78%,56%)]/10 text-[hsl(38,78%,45%)]' : ''}
                        ${isCompleted ? 'text-foreground hover:bg-muted' : ''}
                        ${!isActive && !isCompleted ? 'text-muted-foreground cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      disabled={idx > step}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs
                        ${isActive ? 'bg-[hsl(38,78%,56%)] text-white' : ''}
                        ${isCompleted ? 'bg-teal-100 text-teal-600' : ''}
                        ${!isActive && !isCompleted ? 'bg-muted text-muted-foreground' : ''}
                      `}>
                        {isCompleted ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                      </div>
                      {s.label}
                    </button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Mobile stepper */}
          <div className="lg:hidden flex items-center gap-2 mb-2 overflow-x-auto pb-2">
            {STEPS.map((s, idx) => (
              <div
                key={s.key}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium shrink-0
                  ${idx === step ? 'bg-[hsl(38,78%,56%)] text-white' : ''}
                  ${idx < step ? 'bg-teal-100 text-teal-700' : ''}
                  ${idx > step ? 'bg-muted text-muted-foreground' : ''}
                `}
              >
                {idx < step ? <Check className="w-3 h-3" /> : <span>{idx + 1}</span>}
                <span className={idx === step ? '' : 'hidden sm:inline'}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Step content */}
          <div className="max-w-3xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="rounded-2xl">
                  <CardContent className="p-6">
                    {/* Step 0: Project Basics */}
                    {currentStepKey === 'basics' && (
                      <div className="space-y-5">
                        <div>
                          <h2 className="font-heading text-xl font-semibold mb-1">Project basics</h2>
                          <p className="text-sm text-muted-foreground">Give your project a name and choose your input mode</p>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="project-name">Project name</Label>
                          <Input
                            id="project-name"
                            placeholder="e.g., Product Launch Video"
                            value={formData.name}
                            onChange={(e) => update('name', e.target.value)}
                            className="rounded-xl"
                            data-testid="new-project-name-input"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Input mode</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="new-project-mode-radio">
                            {[
                              { value: 'idea', label: 'Start with an idea', desc: 'AI will generate a script from your brief', icon: Lightbulb },
                              { value: 'script', label: 'Paste your script', desc: 'Skip AI generation, use your own text', icon: FileText },
                              { value: 'research', label: 'Smart Research', desc: 'AI finds today\'s news and suggests video ideas', icon: Globe },
                            ].map((mode) => (
                              <button
                                key={mode.value}
                                onClick={() => update('input_mode', mode.value)}
                                className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-colors
                                  ${formData.input_mode === mode.value
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border hover:border-primary/30'
                                  }
                                `}
                              >
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                                  formData.input_mode === mode.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                                }`}>
                                  <mode.icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <span className="text-sm font-semibold">{mode.label}</span>
                                  <p className="text-xs text-muted-foreground mt-0.5">{mode.desc}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Background type toggle */}
                        <div className="space-y-1.5">
                          <Label>Video background</Label>
                          <div className="grid grid-cols-2 gap-3" data-testid="new-project-background-toggle">
                            <button
                              onClick={() => update('background_type', 'normal')}
                              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors
                                ${formData.background_type === 'normal'
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/30'
                                }
                              `}
                              data-testid="background-normal-btn"
                            >
                              <div className={`w-8 h-8 rounded-lg border-2 shrink-0 ${
                                formData.background_type === 'normal' ? 'bg-white border-primary' : 'bg-white border-border'
                              }`} />
                              <div>
                                <span className="text-sm font-semibold">Normal</span>
                                <p className="text-[11px] text-muted-foreground">White background</p>
                              </div>
                            </button>
                            <button
                              onClick={() => update('background_type', 'green_screen')}
                              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors
                                ${formData.background_type === 'green_screen'
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/30'
                                }
                              `}
                              data-testid="background-green-screen-btn"
                            >
                              <div className={`w-8 h-8 rounded-lg border-2 shrink-0 ${
                                formData.background_type === 'green_screen' ? 'bg-[#008000] border-primary' : 'bg-[#008000] border-border'
                              }`} />
                              <div>
                                <span className="text-sm font-semibold">Green Screen</span>
                                <p className="text-[11px] text-muted-foreground">For chroma key</p>
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {currentStepKey === 'content' && (
                      <div className="space-y-5">
                        {/* Smart Research Mode */}
                        {formData.input_mode === 'research' ? (
                          <div className="space-y-5">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h2 className="font-heading text-xl font-semibold mb-1 flex items-center gap-2">
                                  <Globe className="w-5 h-5 text-[hsl(38,78%,45%)]" />
                                  Smart Research
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                  AI-powered research based on today's India & global news
                                </p>
                              </div>
                              <Button
                                variant="secondary"
                                size="sm"
                                className="rounded-xl gap-1.5 shrink-0"
                                onClick={runSmartResearch}
                                disabled={researchLoading}
                                data-testid="smart-research-refresh-btn"
                              >
                                <RefreshCw className={`w-3.5 h-3.5 ${researchLoading ? 'animate-spin' : ''}`} />
                                Refresh
                              </Button>
                            </div>

                            {/* Loading state */}
                            {researchLoading && (
                              <div className="space-y-4" data-testid="smart-research-loading">
                                <div className="flex items-center gap-3 p-4 bg-[hsl(38,78%,56%)]/5 rounded-xl border border-[hsl(38,78%,56%)]/15">
                                  <Loader2 className="w-5 h-5 text-[hsl(38,78%,45%)] animate-spin shrink-0" />
                                  <div>
                                    <p className="text-sm font-medium">Researching today's markets...</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">Scanning India & global news, analyzing financial trends</p>
                                  </div>
                                </div>
                                {[1, 2, 3].map((i) => (
                                  <Skeleton key={i} className="h-24 rounded-xl" />
                                ))}
                              </div>
                            )}

                            {/* Results */}
                            {researchData && !researchLoading && (
                              <div className="space-y-4">
                                {/* Market summary */}
                                {researchData.market_summary && (
                                  <div className="p-4 bg-[hsl(38,78%,56%)]/5 rounded-xl border border-[hsl(38,78%,56%)]/15" data-testid="smart-research-market-summary">
                                    <div className="flex items-center gap-2 mb-2">
                                      <TrendingUp className="w-4 h-4 text-[hsl(38,78%,45%)]" />
                                      <span className="text-sm font-semibold">Market Overview</span>
                                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-mono">{researchData.generated_at}</Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{researchData.market_summary}</p>
                                  </div>
                                )}

                                {/* Categorized ideas */}
                                <div className="space-y-2" data-testid="smart-research-ideas-list">
                                  {researchData.groups?.map((group) => (
                                    <div key={group.category} className="rounded-xl border overflow-hidden">
                                      {/* Category header */}
                                      <button
                                        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                                          expandedCategory === group.category
                                            ? 'bg-[hsl(38,78%,56%)]/8'
                                            : 'bg-card hover:bg-muted/50'
                                        }`}
                                        onClick={() => setExpandedCategory(expandedCategory === group.category ? null : group.category)}
                                        data-testid={`research-category-${group.category.toLowerCase().replace(/\s+/g, '-')}`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className="font-heading text-sm font-semibold">{group.category}</span>
                                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{group.ideas?.length}</Badge>
                                        </div>
                                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expandedCategory === group.category ? 'rotate-180' : ''}`} />
                                      </button>

                                      {/* Ideas in this category */}
                                      {expandedCategory === group.category && (
                                        <div className="divide-y">
                                          {group.ideas?.map((idea, idx) => {
                                            const isSelected = selectedIdea?.title === idea.title;
                                            return (
                                              <div
                                                key={idx}
                                                className={`p-4 cursor-pointer transition-colors ${
                                                  isSelected
                                                    ? 'bg-[hsl(38,78%,56%)]/10 border-l-2 border-l-[hsl(38,78%,56%)]'
                                                    : 'hover:bg-muted/30'
                                                }`}
                                                onClick={() => selectResearchIdea(idea)}
                                                data-testid={`research-idea-${idx}`}
                                              >
                                                <div className="flex items-start gap-3">
                                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                                                    isSelected ? 'bg-[hsl(38,78%,56%)] text-white' : 'bg-muted text-muted-foreground'
                                                  }`}>
                                                    {isSelected ? <Check className="w-4 h-4" /> : <Newspaper className="w-4 h-4" />}
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-semibold leading-snug">{idea.title}</h4>
                                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{idea.hook}</p>
                                                    {idea.news_context && (
                                                      <div className="flex items-center gap-1.5 mt-2">
                                                        <Globe className="w-3 h-3 text-muted-foreground shrink-0" />
                                                        <span className="text-[11px] text-muted-foreground truncate">{idea.news_context}</span>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>

                                {/* Sources */}
                                {researchData.sources?.length > 0 && (
                                  <details className="text-xs text-muted-foreground">
                                    <summary className="cursor-pointer hover:text-foreground font-medium">
                                      View {researchData.sources.length} news sources
                                    </summary>
                                    <div className="mt-2 space-y-1 pl-3">
                                      {researchData.sources.map((s, i) => (
                                        <div key={i} className="flex items-start gap-1.5">
                                          <span className="text-muted-foreground/50">&bull;</span>
                                          <span className="truncate">{s.title} {s.source && `(${s.source})`}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}
                              </div>
                            )}

                            {/* Selected idea editor */}
                            {selectedIdea && (
                              <div className="space-y-3 pt-2">
                                <div className="amber-divider" />
                                <div className="flex items-center gap-2">
                                  <Check className="w-4 h-4 text-[hsl(38,78%,45%)]" />
                                  <Label className="font-semibold">Selected idea (editable)</Label>
                                </div>
                                <Textarea
                                  value={formData.input_text}
                                  onChange={(e) => update('input_text', e.target.value)}
                                  className="rounded-xl min-h-[140px] resize-y"
                                  data-testid="research-selected-idea-editor"
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <div className="space-y-1.5">
                                    <Label>Tone</Label>
                                    <Select value={formData.tone} onValueChange={(v) => update('tone', v)}>
                                      <SelectTrigger className="rounded-xl" data-testid="research-tone-select">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {TONES.map((t) => (
                                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label>Call to action (optional)</Label>
                                    <Input
                                      placeholder="e.g., Download JioFinance app"
                                      value={formData.cta}
                                      onChange={(e) => update('cta', e.target.value)}
                                      className="rounded-xl"
                                      data-testid="research-cta-input"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                        /* Original Idea/Script Mode */
                        <div className="space-y-5">
                        <div>
                          <h2 className="font-heading text-xl font-semibold mb-1">
                            {formData.input_mode === 'idea' ? 'Your idea' : 'Your script'}
                          </h2>
                          <p className="text-sm text-muted-foreground">
                            {formData.input_mode === 'idea'
                              ? 'Type, speak, or record a video to describe your concept'
                              : 'Type, speak, or record a video with your narration'}
                          </p>
                        </div>

                        {/* Voice / Video capture bar */}
                        <div className="flex flex-wrap gap-2 p-3 bg-muted/50 rounded-xl" data-testid="media-capture-bar">
                          {/* Voice recording */}
                          {isRecordingAudio ? (
                            <Button
                              variant="destructive"
                              size="sm"
                              className="rounded-xl gap-1.5"
                              onClick={stopAudioRecording}
                              data-testid="stop-audio-recording"
                            >
                              <Square className="w-3.5 h-3.5" /> Stop recording
                            </Button>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="rounded-xl gap-1.5"
                              onClick={startAudioRecording}
                              disabled={isRecordingVideo || transcribing}
                              data-testid="start-audio-recording"
                            >
                              <Mic className="w-3.5 h-3.5" /> Record voice
                            </Button>
                          )}

                          {/* Video recording */}
                          {isRecordingVideo ? (
                            <Button
                              variant="destructive"
                              size="sm"
                              className="rounded-xl gap-1.5"
                              onClick={stopVideoRecording}
                              data-testid="stop-video-recording"
                            >
                              <Square className="w-3.5 h-3.5" /> Stop recording
                            </Button>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="rounded-xl gap-1.5"
                              onClick={startVideoRecording}
                              disabled={isRecordingAudio || transcribing}
                              data-testid="start-video-recording"
                            >
                              <Camera className="w-3.5 h-3.5" /> Record video
                            </Button>
                          )}

                          {/* File upload */}
                          <Button
                            variant="secondary"
                            size="sm"
                            className="rounded-xl gap-1.5"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isRecordingAudio || isRecordingVideo || transcribing}
                            data-testid="upload-media-button"
                          >
                            <Upload className="w-3.5 h-3.5" /> Upload file
                          </Button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="audio/*,video/*"
                            className="hidden"
                            onChange={handleFileUpload}
                          />

                          {transcribing && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Transcribing...
                            </div>
                          )}

                          {isRecordingAudio && (
                            <div className="flex items-center gap-1.5 text-xs text-red-600 ml-auto">
                              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse-dot" />
                              Recording audio...
                            </div>
                          )}
                        </div>

                        {/* Video preview when recording */}
                        {isRecordingVideo && (
                          <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
                            <video
                              ref={videoPreviewRef}
                              autoPlay
                              muted
                              playsInline
                              className="w-full h-full object-cover"
                              style={{ transform: 'scaleX(-1)' }}
                            />
                            <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                              <span className="w-2 h-2 rounded-full bg-white animate-pulse-dot" />
                              Recording
                            </div>
                          </div>
                        )}

                        <p className="text-[11px] text-muted-foreground">
                          Recorded audio/video will be automatically transcribed and added to the text box below.
                        </p>

                        <div className="space-y-1.5" data-testid="script-editor">
                          <div className="flex items-center justify-between">
                            <Label>{formData.input_mode === 'idea' ? 'Idea / Brief' : 'Script text'}</Label>
                            <div className="flex gap-2 text-xs text-muted-foreground" data-testid="script-word-count">
                              <span>{wordCount} words</span>
                              <span>&middot;</span>
                              <span>{charCount} chars</span>
                            </div>
                          </div>
                          <Textarea
                            placeholder={formData.input_mode === 'idea'
                              ? 'e.g., A product launch announcement for our new AI writing tool that helps content creators...'
                              : 'Paste your narration script here...'}
                            value={formData.input_text}
                            onChange={(e) => update('input_text', e.target.value)}
                            className="rounded-xl min-h-[180px] resize-y"
                            data-testid="new-project-content-input"
                          />
                        </div>

                        {formData.input_mode === 'idea' && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <Label>Tone</Label>
                              <Select value={formData.tone} onValueChange={(v) => update('tone', v)}>
                                <SelectTrigger className="rounded-xl" data-testid="new-project-tone-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TONES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label>Call to action (optional)</Label>
                              <Input
                                placeholder="e.g., Visit our website"
                                value={formData.cta}
                                onChange={(e) => update('cta', e.target.value)}
                                className="rounded-xl"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                        )}
                      </div>
                    )}

                    {/* Step: Duration */}
                    {currentStepKey === 'duration' && (
                      <div className="space-y-5">
                        <div>
                          <h2 className="font-heading text-xl font-semibold mb-1">Video duration</h2>
                          <p className="text-sm text-muted-foreground">Choose how long your video should be</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="new-project-duration-toggle">
                          {[
                            { value: '60', label: '60 seconds', desc: 'Short-form, punchy delivery. ~150 words.', icon: '1m' },
                            { value: '90', label: '90 seconds', desc: 'More context, still conversational. ~220 words.', icon: '1.5m' },
                          ].map((d) => (
                            <button
                              key={d.value}
                              onClick={() => update('duration_mode', d.value)}
                              className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-colors
                                ${formData.duration_mode === d.value
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/30'
                                }
                              `}
                            >
                              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-heading font-bold
                                ${formData.duration_mode === d.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
                              `}>
                                {d.icon}
                              </div>
                              <div className="text-center">
                                <span className="text-sm font-semibold">{d.label}</span>
                                <p className="text-xs text-muted-foreground mt-0.5">{d.desc}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Step: Script Review (only for idea/research) */}
                    {currentStepKey === 'script_review' && (
                      <div className="space-y-5">
                        <div>
                          <h2 className="font-heading text-xl font-semibold mb-1 flex items-center gap-2">
                            <Wand2 className="w-5 h-5 text-[hsl(38,78%,45%)]" />
                            Review your script
                          </h2>
                          <p className="text-sm text-muted-foreground">
                            AI has generated a script from your idea. Review, edit, and approve before continuing.
                          </p>
                        </div>

                        {/* Loading state */}
                        {scriptGenerating && (
                          <div className="space-y-4" data-testid="script-review-loading">
                            <div className="flex items-center gap-3 p-4 bg-[hsl(38,78%,56%)]/5 rounded-xl border border-[hsl(38,78%,56%)]/15">
                              <Loader2 className="w-5 h-5 text-[hsl(38,78%,45%)] animate-spin shrink-0" />
                              <div>
                                <p className="text-sm font-medium">Generating your script...</p>
                                <p className="text-xs text-muted-foreground mt-0.5">AI is crafting a {formData.duration_mode}-second script based on your idea</p>
                              </div>
                            </div>
                            <Skeleton className="h-48 rounded-xl" />
                            <Skeleton className="h-8 w-40 rounded-xl" />
                          </div>
                        )}

                        {/* Script editor */}
                        {scriptReviewed && reviewedScript && (
                          <div className="space-y-4" data-testid="script-review-editor">
                            <div className="flex items-center justify-between">
                              <Badge variant="secondary" className="text-xs gap-1.5">
                                <Check className="w-3 h-3" />
                                Script generated
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {reviewedScript.length} characters &middot; ~{Math.round(reviewedScript.split(/\s+/).length / 2.5)}s reading time
                              </span>
                            </div>
                            <Textarea
                              value={reviewedScript}
                              onChange={(e) => setReviewedScript(e.target.value)}
                              className="rounded-xl min-h-[280px] resize-y font-mono text-sm leading-relaxed"
                              placeholder="Your generated script will appear here..."
                              data-testid="script-review-textarea"
                            />
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <Edit3 className="w-3.5 h-3.5 shrink-0" />
                              <span>You can freely edit the script above. Click <strong>Next</strong> when you're happy with it.</span>
                            </div>

                            {/* Regenerate button */}
                            <Button
                              variant="secondary"
                              size="sm"
                              className="rounded-xl gap-1.5"
                              onClick={() => {
                                setScriptReviewed(false);
                                setReviewedScript('');
                                generateScriptForReview();
                              }}
                              disabled={scriptGenerating}
                              data-testid="script-review-regenerate-btn"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${scriptGenerating ? 'animate-spin' : ''}`} />
                              Regenerate script
                            </Button>
                          </div>
                        )}

                        {/* Error/empty state */}
                        {!scriptGenerating && !scriptReviewed && (
                          <div className="text-center py-8">
                            <p className="text-sm text-muted-foreground mb-3">Script generation hasn't started yet.</p>
                            <Button
                              onClick={generateScriptForReview}
                              className="rounded-xl gap-1.5"
                              data-testid="script-review-generate-btn"
                            >
                              <Wand2 className="w-4 h-4" />
                              Generate script
                            </Button>
                          </div>
                        )}
                      </div>
                    )}


                    {/* Step: Avatar */}
                    {currentStepKey === 'avatar' && (
                      <div className="space-y-5">
                        <div>
                          <h2 className="font-heading text-xl font-semibold mb-1">Choose an avatar</h2>
                          <p className="text-sm text-muted-foreground">Select the AI avatar for your video</p>
                        </div>
                        <Input
                          placeholder="Search avatars..."
                          value={avatarSearch}
                          onChange={(e) => setAvatarSearch(e.target.value)}
                          className="rounded-xl"
                          data-testid="avatar-search-input"
                        />
                        {loadingAvatars ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                              <Skeleton key={i} className="h-36 rounded-xl" />
                            ))}
                          </div>
                        ) : (
                          <ScrollArea className="h-[400px]" data-testid="avatar-grid">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pr-3">
                              {filteredAvatars.map((avatar) => (
                                <button
                                  key={avatar.avatar_id}
                                  onClick={() => update('avatar_id', avatar.avatar_id)}
                                  className={`relative flex flex-col items-center p-3 rounded-xl border-2 transition-colors
                                    ${formData.avatar_id === avatar.avatar_id
                                      ? 'border-primary bg-primary/5'
                                      : 'border-border hover:border-primary/30'
                                    }
                                  `}
                                  data-testid="avatar-tile"
                                >
                                  {formData.avatar_id === avatar.avatar_id && (
                                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                      <Check className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                  <div className="w-16 h-16 rounded-full overflow-hidden bg-muted mb-2">
                                    {avatar.preview_image_url ? (
                                      <img
                                        src={avatar.preview_image_url}
                                        alt={avatar.avatar_name}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                        <User className="w-6 h-6" />
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-xs font-medium text-center truncate w-full">{avatar.avatar_name}</span>
                                  {avatar.gender && (
                                    <span className="text-[10px] text-muted-foreground capitalize">{avatar.gender}</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </ScrollArea>
                        )}
                      </div>
                    )}

                    {/* Step: Voice */}
                    {currentStepKey === 'voice' && (
                      <div className="space-y-5">
                        <div>
                          <h2 className="font-heading text-xl font-semibold mb-1">Choose a voice</h2>
                          <p className="text-sm text-muted-foreground">Select the voice for your avatar's narration</p>
                        </div>
                        <Input
                          placeholder="Search voices..."
                          value={voiceSearch}
                          onChange={(e) => setVoiceSearch(e.target.value)}
                          className="rounded-xl"
                          data-testid="voice-search-input"
                        />
                        {loadingVoices ? (
                          <div className="space-y-2">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <Skeleton key={i} className="h-16 rounded-xl" />
                            ))}
                          </div>
                        ) : (
                          <ScrollArea className="h-[400px]" data-testid="voice-list">
                            <div className="space-y-2 pr-3">
                              {filteredVoices.map((voice) => (
                                <button
                                  key={voice.voice_id}
                                  onClick={() => update('voice_id', voice.voice_id)}
                                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors
                                    ${formData.voice_id === voice.voice_id
                                      ? 'border-primary bg-primary/5'
                                      : 'border-border hover:border-primary/30'
                                    }
                                  `}
                                  data-testid="voice-select-radio"
                                >
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                                    ${formData.voice_id === voice.voice_id ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
                                  `}>
                                    <Volume2 className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold">{voice.name}</span>
                                      {voice.gender && (
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 capitalize">{voice.gender}</Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground capitalize">
                                      {[voice.accent, voice.age, voice.use_case].filter(Boolean).join(' · ')}
                                    </p>
                                  </div>
                                  {voice.preview_url && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="rounded-lg h-8 w-8 shrink-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleVoicePreview(voice.voice_id, voice.preview_url);
                                      }}
                                      data-testid="voice-preview-button"
                                    >
                                      {playingVoice === voice.voice_id ? (
                                        <Pause className="w-4 h-4" />
                                      ) : (
                                        <Play className="w-4 h-4" />
                                      )}
                                    </Button>
                                  )}
                                  {formData.voice_id === voice.voice_id && (
                                    <Check className="w-5 h-5 text-primary shrink-0" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </ScrollArea>
                        )}
                      </div>
                    )}

                    {/* Step: Generate */}
                    {currentStepKey === 'generate' && (
                      <div className="space-y-5">
                        <div>
                          <h2 className="font-heading text-xl font-semibold mb-1">Review & generate</h2>
                          <p className="text-sm text-muted-foreground">
                            Review your settings and start the generation pipeline
                          </p>
                        </div>

                        {/* Summary */}
                        <div className="space-y-3">
                          {[
                            { label: 'Project', value: formData.name },
                            { label: 'Mode', value: formData.input_mode === 'idea' ? 'Idea → AI Script' : formData.input_mode === 'research' ? 'Smart Research → AI Script' : 'Custom Script' },
                            { label: 'Duration', value: `${formData.duration_mode} seconds` },
                            { label: 'Tone', value: formData.tone },
                            { label: 'Background', value: formData.background_type === 'green_screen' ? 'Green Screen' : 'Normal' },
                            { label: 'Avatar', value: avatars.find(a => a.avatar_id === formData.avatar_id)?.avatar_name || formData.avatar_id },
                            { label: 'Voice', value: voices.find(v => v.voice_id === formData.voice_id)?.name || formData.voice_id },
                            ...(needsScriptReview && reviewedScript ? [{ label: 'Script', value: `${reviewedScript.length} chars (reviewed)` }] : []),
                          ].map((item) => (
                            <div key={item.label} className="flex items-center justify-between py-2 border-b last:border-0">
                              <span className="text-sm text-muted-foreground">{item.label}</span>
                              <span className="text-sm font-medium">{item.value}</span>
                            </div>
                          ))}
                        </div>

                        {/* Pipeline info */}
                        <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                          <p className="text-sm font-medium">Generation pipeline</p>
                          <div className="space-y-1.5 text-xs text-muted-foreground">
                            {needsScriptReview && (
                              <div className="flex items-center gap-2 text-teal-600">
                                <Check className="w-3.5 h-3.5" />
                                <span>Script generated & reviewed</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <User className="w-3.5 h-3.5" />
                              <span>1. Avatar video render with TTS (~2-4 min)</span>
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={handleCreateAndGenerate}
                          disabled={isProcessing}
                          className="w-full rounded-xl gap-2 h-12 text-base"
                          data-testid="render-video-button"
                        >
                          {isProcessing ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Starting render...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-5 h-5" />
                              Generate video
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Navigation */}
                {currentStepKey !== 'generate' && (
                  <div className="flex items-center justify-between mt-4">
                    <Button
                      variant="ghost"
                      onClick={handleBack}
                      disabled={step === 0}
                      className="rounded-xl gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" /> Back
                    </Button>
                    <Button
                      onClick={handleNext}
                      disabled={!canProceed() || scriptGenerating}
                      className="rounded-xl gap-1"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
}
